import React, { useState, useEffect } from 'react';
import { Eye, EyeOff, Search, Lock, Globe, X, Plus } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from '../../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminProductVisibility = () => {
  const [items, setItems] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [editProduct, setEditProduct] = useState(null);
  const [draftVisibility, setDraftVisibility] = useState('public');
  const [draftAllowed, setDraftAllowed] = useState([]);
  const [companyToAdd, setCompanyToAdd] = useState('');
  const [newCompanyInput, setNewCompanyInput] = useState('');

  const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [vRes, cRes] = await Promise.all([
        axios.get(`${API}/admin/visibility`, auth()),
        axios.get(`${API}/admin/companies`, auth()),
      ]);
      setItems(vRes.data || []);
      setCompanies(cRes.data || []);
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Failed to load visibility data', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const openEdit = (product) => {
    setEditProduct(product);
    setDraftVisibility(product.visibility || 'public');
    setDraftAllowed(product.allowed_companies || []);
    setCompanyToAdd('');
    setNewCompanyInput('');
  };

  const closeEdit = () => {
    setEditProduct(null);
  };

  const addAllowedCompany = (raw) => {
    const c = (raw || '').trim().toLowerCase();
    if (!c) return;
    if (draftAllowed.includes(c)) return;
    setDraftAllowed([...draftAllowed, c]);
  };

  const removeAllowedCompany = (c) => {
    setDraftAllowed(draftAllowed.filter(x => x !== c));
  };

  const saveVisibility = async () => {
    try {
      await axios.put(
        `${API}/admin/products/${editProduct.id}/visibility`,
        { visibility: draftVisibility, allowed_companies: draftAllowed },
        auth()
      );
      toast({ title: 'Visibility updated', description: editProduct.name });
      closeEdit();
      fetchAll();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Failed to update visibility', variant: 'destructive' });
    }
  };

  const filtered = items.filter(p => !search || p.name.toLowerCase().includes(search.toLowerCase()));

  const restrictedCount = items.filter(p => p.visibility === 'restricted').length;
  const publicCount = items.length - restrictedCount;

  return (
    <div className="p-4 sm:p-8" data-testid="admin-visibility-page">
      <div className="mb-6">
        <h1 className="text-2xl sm:text-4xl font-bold text-slate-800 flex items-center gap-2">
          <Lock className="h-7 w-7 text-purple-600" /> Product Visibility
        </h1>
        <p className="text-sm text-slate-500 mt-1">Control which organizations can see and purchase each product. Restricted products are hidden from the public catalog and from buyers outside the allowed companies.</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
        <Card className="border-2"><CardContent className="p-4 text-center"><p className="text-3xl font-bold text-slate-700" data-testid="visibility-total">{items.length}</p><p className="text-xs text-slate-500">Total products</p></CardContent></Card>
        <Card className="border-2"><CardContent className="p-4 text-center"><p className="text-3xl font-bold text-emerald-600">{publicCount}</p><p className="text-xs text-slate-500">Public</p></CardContent></Card>
        <Card className="border-2"><CardContent className="p-4 text-center"><p className="text-3xl font-bold text-amber-600">{restrictedCount}</p><p className="text-xs text-slate-500">Restricted</p></CardContent></Card>
      </div>

      <Card className="mb-6 border-2">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search products..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="visibility-search"
            />
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center py-12"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-600"></div></div>
      ) : (
        <Card className="border-2">
          <CardHeader><CardTitle>Products ({filtered.length})</CardTitle></CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left p-3">Product</th>
                    <th className="text-left p-3">Category</th>
                    <th className="text-left p-3">Visibility</th>
                    <th className="text-left p-3">Allowed Organizations</th>
                    <th className="text-left p-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(p => (
                    <tr key={p.id} className="border-t hover:bg-slate-50" data-testid={`visibility-row-${p.id}`}>
                      <td className="p-3">
                        <div className="flex items-center gap-3">
                          {p.image ? <img src={p.image} alt="" className="w-10 h-10 rounded object-cover" /> : null}
                          <span className="font-medium">{p.name}</span>
                        </div>
                      </td>
                      <td className="p-3 text-slate-600 capitalize">{(p.category || '').replace(/-/g, ' ')}</td>
                      <td className="p-3">
                        {p.visibility === 'restricted' ? (
                          <span className="inline-flex items-center gap-1 bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full text-xs font-medium"><EyeOff className="h-3 w-3" />Restricted</span>
                        ) : (
                          <span className="inline-flex items-center gap-1 bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full text-xs font-medium"><Globe className="h-3 w-3" />Public</span>
                        )}
                      </td>
                      <td className="p-3">
                        {p.visibility === 'restricted' ? (
                          (p.allowed_companies || []).length === 0 ? (
                            <span className="text-xs text-red-500">⚠ No companies allowed (nobody can see this)</span>
                          ) : (
                            <div className="flex flex-wrap gap-1">
                              {(p.allowed_companies || []).map(c => <span key={c} className="text-xs bg-slate-100 px-2 py-0.5 rounded capitalize">{c}</span>)}
                            </div>
                          )
                        ) : <span className="text-slate-400 text-xs">—</span>}
                      </td>
                      <td className="p-3">
                        <Button size="sm" variant="outline" onClick={() => openEdit(p)} data-testid={`edit-visibility-${p.id}`}>
                          <Eye className="h-4 w-4 mr-1" /> Edit
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Edit Dialog */}
      <Dialog open={!!editProduct} onOpenChange={(open) => !open && closeEdit()}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Visibility — {editProduct?.name}</DialogTitle></DialogHeader>
          {editProduct && (
            <div className="space-y-4">
              <div>
                <Label>Visibility</Label>
                <div className="flex gap-2 mt-2">
                  <button
                    type="button"
                    className={`flex-1 px-3 py-2 rounded border-2 text-sm font-medium ${draftVisibility === 'public' ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-slate-200 text-slate-600'}`}
                    onClick={() => setDraftVisibility('public')}
                    data-testid="visibility-public-btn"
                  >
                    <Globe className="h-4 w-4 inline mr-1" />Public
                  </button>
                  <button
                    type="button"
                    className={`flex-1 px-3 py-2 rounded border-2 text-sm font-medium ${draftVisibility === 'restricted' ? 'border-amber-500 bg-amber-50 text-amber-700' : 'border-slate-200 text-slate-600'}`}
                    onClick={() => setDraftVisibility('restricted')}
                    data-testid="visibility-restricted-btn"
                  >
                    <Lock className="h-4 w-4 inline mr-1" />Restricted
                  </button>
                </div>
                <p className="text-xs text-slate-500 mt-2">
                  {draftVisibility === 'public'
                    ? 'Visible to everyone in the public catalog.'
                    : 'Hidden from the public catalog and only visible/purchasable by buyers from listed organizations.'}
                </p>
              </div>

              {draftVisibility === 'restricted' && (
                <div>
                  <Label>Allowed Organizations</Label>
                  <div className="flex flex-wrap gap-1 mt-2 mb-3">
                    {draftAllowed.length === 0 && <span className="text-xs text-red-500">No companies allowed yet — add at least one.</span>}
                    {draftAllowed.map(c => (
                      <span key={c} className="inline-flex items-center gap-1 bg-purple-100 text-purple-700 px-2 py-0.5 rounded text-xs capitalize" data-testid={`allowed-${c}`}>
                        {c}
                        <button type="button" onClick={() => removeAllowedCompany(c)} className="hover:text-red-500">
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div className="flex gap-2">
                      <Select value={companyToAdd} onValueChange={setCompanyToAdd}>
                        <SelectTrigger><SelectValue placeholder="Add registered..." /></SelectTrigger>
                        <SelectContent>
                          {companies.length === 0 && <SelectItem value="__none" disabled>No registered companies</SelectItem>}
                          {companies.map(c => <SelectItem key={c.company_normalized} value={c.company_normalized}>{c.company}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <Button type="button" variant="outline" onClick={() => { addAllowedCompany(companyToAdd); setCompanyToAdd(''); }}><Plus className="h-4 w-4" /></Button>
                    </div>
                    <div className="flex gap-2">
                      <Input placeholder="...or type new company" value={newCompanyInput} onChange={(e) => setNewCompanyInput(e.target.value)} data-testid="new-allowed-company-input" />
                      <Button type="button" variant="outline" onClick={() => { addAllowedCompany(newCompanyInput); setNewCompanyInput(''); }}><Plus className="h-4 w-4" /></Button>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex gap-2 justify-end pt-2">
                <Button variant="outline" onClick={closeEdit}>Cancel</Button>
                <Button className="bg-purple-600 hover:bg-purple-700 text-white" onClick={saveVisibility} data-testid="save-visibility-btn">Save</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminProductVisibility;
